/**
 * ClientOfferPage
 *
 * Internal SSO client offer view for /offers/:offerId/client.
 * Data sources:
 *   - GET /offers/:id/client-view — version metadata + diff (null on first view)
 *   - GET /offers/:id/versions/:versionId — snapshot content for rendering
 *
 * Traceability: REQ-7.6 → offer-versioning-diff / Client "What Changed" Panel UI,
 *               Version-Specific Accept Button UI
 */
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { offerVersionEndpoints, parseApiError } from '@ooh/contracts';
import type {
  ClientOfferViewUnit,
  CarryForwardPrefill,
  OfferPeriodInquiryUnitSelection,
} from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { useCurrentUser } from '../../plugs/current-user';
import { useAcceptVersion, useSubmitPeriodInquiry } from './hooks/use-client-offer-mutations';
import { SharedClientOfferContent } from './shared-client-offer-content';
import type { OpenClientUnitDetail } from './shared-client-offer-content';
import {
  ClientOfferPageError,
  ClientOfferPageLoading,
  ClientOfferPageNotFound,
} from './client-offer-page-states';
import { ClientOfferReadOnlyContent } from './client-offer-read-only-content';
import {
  useClientOfferSelections,
  buildUnitDecisionsPayload,
  useWarnOnDroppedDecisions,
} from './hooks/use-client-offer-selections';
import { usePeriodInquirySummary } from './hooks/use-period-inquiry-summary';
import { useAcceptErrorHandler } from './hooks/use-accept-error-handler';
import { buildMediaUnitIdLookup } from './lib/build-media-unit-id-lookup';
import { buildEnrichedUnitById } from './lib/build-enriched-unit-lookup';

export type { OpenClientUnitDetail };

// ─── Main client offer content (authenticated path) ───────────────────────────

interface ClientOfferContentProps {
  offerId: string;
  versionId: string;
  versionNumber: number;
  publishedAt: Date;
  acceptedAt: Date | null | undefined;
  diff: import('@ooh/contracts').ClientDiffItem[] | null;
  snapshot: import('@ooh/contracts').OfferVersionWithSnapshot;
  enrichedUnits: ClientOfferViewUnit[];
  validUntil: Date;
  defaultDateFrom: Date | null;
  defaultDateTo: Date | null;
  carryForwardPrefill: CarryForwardPrefill;
  onOpenUnitDetail: OpenClientUnitDetail;
}

function ClientOfferContent({
  offerId,
  versionId,
  versionNumber,
  publishedAt,
  acceptedAt,
  diff,
  snapshot,
  enrichedUnits,
  validUntil,
  defaultDateFrom,
  defaultDateTo,
  carryForwardPrefill,
  onOpenUnitDetail,
}: ClientOfferContentProps) {
  const { user } = useCurrentUser();
  const acceptMutation = useAcceptVersion();
  const submitInquiryMutation = useSubmitPeriodInquiry();

  const enrichedById = useMemo(() => buildEnrichedUnitById(enrichedUnits), [enrichedUnits]);

  // No acceptDisabled overlay here — the per-unit Accept control is gated by
  // unit.canAccept (useClientOfferReviewUnits, live-recomputed per granularity
  // change). A prior acceptableUnitIds/applyAcceptGate overlay computed from a
  // client-item list here was frozen at INITIAL_GRANULARITY and only ever fed
  // "select all"/"clear all" (use-client-offer-selections.ts's nonSoldItems) —
  // removed rather than fixed (PR #985 review finding).
  const mediaUnitIdByRowId = useMemo(
    () => buildMediaUnitIdLookup(snapshot.snapshotData.units),
    [snapshot.snapshotData.units],
  );

  // ─── Decision + period + granularity state (extracted hook, owns the shared
  // period axis — see useClientOfferSelections) ─────────────────────────────
  const {
    decisions,
    selectedPeriods,
    granularity,
    periods,
    handleDecisionChange,
    handleGranularityChange,
    handlePeriodSelect,
    lockedRowIds,
    clearPersistedSelections,
  } = useClientOfferSelections({
    enrichedById,
    acceptedAt: acceptedAt ?? null,
    validUntil,
    carryForwardPrefill,
    campaignFrom: defaultDateFrom,
    campaignTo: defaultDateTo,
    persistence: { scope: 'sso', scopeId: offerId, versionId },
  });

  // CU-86cay88zz: warn instead of silently losing units whose decision was
  // dropped from the built unitDecisions payload. Shared hook (also used by
  // public-offer-page.tsx) so accept/reject and the SSO/public-link flows
  // cannot diverge in the warning trigger, message, or i18n key.
  const warnOnDroppedDecisions = useWarnOnDroppedDecisions();
  const handleAcceptError = useAcceptErrorHandler();

  const handleAccept = () => {
    const unitDecisions = buildUnitDecisionsPayload(
      decisions,
      selectedPeriods,
      mediaUnitIdByRowId,
      periods,
    );
    warnOnDroppedDecisions(decisions, unitDecisions);
    acceptMutation.mutate(
      { offerId, versionId, unitDecisions },
      { onSuccess: clearPersistedSelections, onError: handleAcceptError },
    );
  };

  // ─── Shared period-inquiry submit action — also used by public-offer-page.tsx so
  // the two routes cannot drift on what gets submitted (qual-dry-violation).
  const submitInquiry = useCallback(
    (
      selection: OfferPeriodInquiryUnitSelection[],
      callbacks: { onSuccess: () => void; onError: (error: unknown) => void },
    ) => {
      submitInquiryMutation.mutate({ offerId, selection }, callbacks);
    },
    [offerId, submitInquiryMutation],
  );
  const { submitPeriodInquiry } = usePeriodInquirySummary({
    selectedPeriods,
    submit: submitInquiry,
  });

  return (
    <SharedClientOfferContent
      versionNumber={versionNumber}
      publishedAt={publishedAt}
      acceptedAt={acceptedAt ?? null}
      diff={diff}
      onAccept={handleAccept}
      acceptMutation={acceptMutation}
      clientName={snapshot.snapshotData.clientName}
      offerTitle={snapshot.snapshotData.title}
      readOnly={false}
      validUntil={validUntil}
      decisions={decisions}
      onDecisionChange={handleDecisionChange}
      selectedPeriods={selectedPeriods}
      onPeriodSelect={handlePeriodSelect}
      snapshotUnits={snapshot.snapshotData.units}
      enrichedUnitsById={enrichedById}
      showSupplierIdentity={false}
      campaignFrom={defaultDateFrom}
      campaignTo={defaultDateTo}
      accessMode="Zalogowany (SSO)"
      loggedInEmail={user?.email ?? ''}
      periodInquiry={{ onSubmit: submitPeriodInquiry, isPending: submitInquiryMutation.isPending }}
      onOpenUnitDetail={onOpenUnitDetail}
      lockedRowIds={lockedRowIds}
      granularity={granularity}
      onGranularityChange={handleGranularityChange}
    />
  );
}

// ─── Page orchestrator ────────────────────────────────────────────────────────

interface ClientOfferPageProps {
  offerId: string;
  /** Opens the client media-unit detail route; the app owns the router. */
  onOpenUnitDetail: OpenClientUnitDetail;
}

function resolveClientViewError(error: unknown): React.ReactElement {
  const statusCode = parseApiError(error).statusCode;
  if (statusCode === 404) {
    return <ClientOfferPageNotFound />;
  }
  return <ClientOfferPageError />;
}

/**
 * Branches between the interactive and post-acceptance read-only render — extracted so
 * `ClientOfferPage` itself stays a simple loading/error/data dispatcher (lint `complexity`
 * budget). A withdrawn acceptance (rejectedAt also set) falls through to the interactive
 * render, matching the public route's identical acceptedAt/rejectedAt guard. `rejectedAt`
 * itself is NOT forwarded to that render: this product has no client-facing reject
 * control, so rejectedAt on this route only ever means a staff withdrawal, never a
 * decision the client made — showing the "rejected" banner here would blame the client
 * for a call staff made on their behalf (client-offer-decision-flow bug fix).
 */
function renderClientOfferContent(
  offerId: string,
  data: import('@ooh/contracts').ClientOfferViewResponse,
  snapshot: import('@ooh/contracts').OfferVersionWithSnapshot,
  onOpenUnitDetail: OpenClientUnitDetail,
): React.ReactElement {
  const {
    version,
    diff,
    units,
    validUntil,
    defaultDateFrom,
    defaultDateTo,
    carryForwardPrefill,
    recordedDecisions,
  } = data;

  if (version.acceptedAt != null && version.rejectedAt == null) {
    return (
      <ClientOfferReadOnlyContent
        versionId={String(version.id)}
        versionNumber={version.versionNumber}
        publishedAt={new Date(version.publishedAt)}
        acceptedAt={version.acceptedAt}
        snapshot={snapshot}
        enrichedUnits={units}
        defaultDateFrom={defaultDateFrom}
        defaultDateTo={defaultDateTo}
        recordedDecisions={recordedDecisions}
        onOpenUnitDetail={onOpenUnitDetail}
      />
    );
  }

  return (
    <ClientOfferContent
      offerId={offerId}
      versionId={String(version.id)}
      versionNumber={version.versionNumber}
      publishedAt={new Date(version.publishedAt)}
      acceptedAt={version.acceptedAt ?? null}
      diff={diff}
      snapshot={snapshot}
      enrichedUnits={units}
      validUntil={validUntil}
      defaultDateFrom={defaultDateFrom}
      defaultDateTo={defaultDateTo}
      carryForwardPrefill={carryForwardPrefill ?? []}
      onOpenUnitDetail={onOpenUnitDetail}
    />
  );
}

function ClientOfferPageData({ offerId, onOpenUnitDetail }: ClientOfferPageProps) {
  const { data: clientView } = useApiSuspenseQuery(offerVersionEndpoints.clientView, {
    id: Number(offerId),
  });
  const { data: snapshot } = useApiSuspenseQuery(offerVersionEndpoints.getSnapshot, {
    id: Number(offerId),
    versionId: clientView.version.id,
  });

  return renderClientOfferContent(offerId, clientView, snapshot, onOpenUnitDetail);
}

export function ClientOfferPage(props: ClientOfferPageProps) {
  return (
    <QueryBoundary
      fallback={<ClientOfferPageLoading />}
      renderError={(error) => resolveClientViewError(error)}
    >
      <ClientOfferPageData {...props} />
    </QueryBoundary>
  );
}
